import { ImagesHolder } from "../../image_loader/images_holder.js";
import { ImgID } from "../../image_loader/img_id.js";
import { NineSliceDrawer } from "../../image_loader/nine_slicer/nine_slice_drawer.js";
import { UIElement } from "./ui_element.js";
import { applyNineSliceBorderImage, nineSliceBorderImage } from "../dom_backend/dom_nine_slice.js";

/**
 * Pill progress fill seen through a pill window the size of the whole fill area.
 * The fill pill is never narrower than its height: below that it slides in from
 * behind the window's left edge, so a tiny share is a sliver that follows the
 * track's rounded end instead of sticking out of it or squashing its caps.
 * The nine-slice rule must cut the art's caps at its semicircles (cap = height / 2).
 * DOM: rounded `overflow: hidden` window over a border-image strip; canvas: a clip.
 */
export class PillProgressFill extends UIElement
{
  private readonly img_id: ImgID;
  private readonly rule: string;
  private share: number;
  private dom_window: HTMLDivElement | null = null;
  private dom_strip: HTMLDivElement | null = null;
  private dom_last_key = "";
  private dom_last_spec = "";

  /** `share` is 0..1 of the element's width. */
  constructor( ctx: CanvasRenderingContext2D, x: number, y: number, width: number, height: number, img_id: ImgID, rule: string, share: number )
  {
    super( ctx, x, y, width, height, false );
    this.img_id = img_id;
    this.rule = rule;
    this.share = Math.max( 0, Math.min( 1, share ) );
  }

  public name(): string
  {
    return "PillProgressFill";
  }

  public setShare( share: number ): void
  {
    const next = Math.max( 0, Math.min( 1, share ) );
    if ( next === this.share )
      return;
    this.share = next;
    this.setShouldBeRendered();
  }

  /** Left edge and width of the fill pill, in px relative to the element. */
  private strip(): { left: number; width: number }
  {
    const h = this.rect.getH();
    let end = this.rect.getW() * this.share;
    // Below a full circle the sliver is boosted (x2 at the start, easing back
    // to the true size at one circle), so a small share does not read as less.
    if ( end < h )
      end = end * ( 2 - end / h );
    const width = Math.max( end, h );
    return { left: end - width, width };
  }

  private caps( h: number )
  {
    return { top_left: { width: h / 2, height: 0 }, bottom_right: { width: h / 2, height: 0 } };
  }

  protected override syncDomContent( node: HTMLElement ): boolean
  {
    const w = this.rect.getW();
    const h = this.rect.getH();
    const { left, width } = this.strip();
    const key = `${w}|${h}|${left}|${width}`;
    if ( key === this.dom_last_key )
      return true;

    if ( !this.dom_window || !this.dom_strip )
    {
      const window_el = document.createElement( "div" );
      const ws = window_el.style;
      ws.position = "absolute";
      ws.left = "0";
      ws.top = "0";
      ws.overflow = "hidden";
      ws.pointerEvents = "none";

      const strip = document.createElement( "div" );
      const ss = strip.style;
      ss.position = "absolute";
      ss.top = "0";

      window_el.appendChild( strip );
      node.appendChild( window_el );
      this.dom_window = window_el;
      this.dom_strip = strip;
    }

    const ws = this.dom_window.style;
    ws.width = `${w}px`;
    ws.height = `${h}px`;
    ws.borderRadius = `${h / 2}px`;
    ws.display = this.share > 0 ? "" : "none";

    if ( this.share > 0 )
    {
      const spec = nineSliceBorderImage( this.img_id, this.rule, width, h, this.caps( h ) );
      if ( !spec ) return false;
      const ss = this.dom_strip.style;
      ss.left = `${left}px`;
      ss.width = `${width}px`;
      ss.height = `${h}px`;
      // The caps depend on the height only, so a moving fill rewrites just left/width.
      if ( spec.key !== this.dom_last_spec )
      {
        applyNineSliceBorderImage( ss, spec );
        this.dom_last_spec = spec.key;
      }
    }

    this.dom_last_key = key;
    return true;
  }

  public render(): void
  {
    if ( !this.shouldBeRendered() )
      return;

    this.startRender();
    this.applyTransform();

    if ( !this.isDomNative() && this.share > 0 )
    {
      const x = -this.rect.getW2();
      const y = -this.rect.getH2();
      const h = this.rect.getH();
      const { left, width } = this.strip();

      this.ctx.save();
      this.ctx.beginPath();
      this.ctx.roundRect( x, y, this.rect.getW(), h, h / 2 );
      this.ctx.clip();
      NineSliceDrawer.drawNineSliceImage(
        this.ctx, ImagesHolder.getImage( this.img_id ), this.rule, x + left, y, width, h, this.caps( h )
      );
      this.ctx.restore();
    }

    this.finishRender();
  }
}
